import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DEFAULT_HISTORY_FILTERS,
  getHistoryOperationFilters,
  TRANSACTION_TYPE_FILTERS,
} from '../../../assets/constants/historyData';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import useHistoryFilterMetadata from '../../../assets/hooks/useHistoryFilterMetadata';
import {
  fetchTransactionsPage,
  getTransactionRouteSnapshot,
  groupTransactionsByDate,
} from '../../../assets/services/transactions';
import {
  ShootingStarIcon,
} from '../../../assets/icons/icons';
import { isUnlockSessionReady } from '../../../assets/services/unlockSession';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import MiniPreloader from '../../MiniPreloader/MiniPreloader';
import TxCard from '../../shared/TxCard/TxCard';
import HistoryFilters, {
  getCurrentHistoryMonthRange,
  getHistoryMonthRange,
} from '../HistoryFilters/HistoryFilters';
import HistorySummaryFilter from './HistorySummaryFilter';
import { getHistoryEmptyState } from './historyEmptyState';
import {
  DEFAULT_HISTORY_LIST_TRANSITION,
  getHistoryListTransition,
  HISTORY_LIST_VARIANTS,
  HISTORY_PANEL_TRANSITION,
} from './historyMotion';
import './History.css';

// TODO: Enable export and search when backend support is ready.
const HISTORY_ADVANCED_TOOLS_ENABLED = false;
const HISTORY_PAGE_LIMIT = 25;
const HISTORY_SCROLL_IDLE_DELAY = 180;

function getHistoryGroupMonthKey(group, now = dayjs()) {
  if (group?.key === 'today') {
    return now.format('YYYY-MM');
  }

  if (group?.key === 'yesterday') {
    return now.subtract(1, 'day').format('YYYY-MM');
  }

  const groupDate = group?.key ? dayjs(group.key) : null;

  if (groupDate?.isValid()) {
    return groupDate.format('YYYY-MM');
  }

  const firstTransaction = group?.items?.[0];
  const transactionDateValue = firstTransaction?.createdAt
    || firstTransaction?.created_at
    || firstTransaction?.utc_datetime
    || firstTransaction?.date;
  const transactionDate = transactionDateValue ? dayjs(transactionDateValue) : null;

  return transactionDate?.isValid() ? transactionDate.format('YYYY-MM') : '';
}

function getVisibleHistoryMonthKey(listNode) {
  const listBounds = listNode?.getBoundingClientRect();
  const groups = Array.from(
    listNode?.querySelectorAll('.history__group[data-history-month]') || []
  );

  if (!listBounds || groups.length === 0) {
    return '';
  }

  const probeY = listBounds.top + Math.min(24, listBounds.height * 0.08);
  const groupAtProbe = groups.find((group) => {
    const bounds = group.getBoundingClientRect();
    return bounds.top <= probeY && bounds.bottom > probeY;
  });
  const firstVisibleGroup = groupAtProbe || groups.find((group) => {
    const bounds = group.getBoundingClientRect();
    return bounds.bottom > listBounds.top && bounds.top < listBounds.bottom;
  });

  return firstVisibleGroup?.dataset.historyMonth || '';
}

function History() {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();
  const listRef = useRef(null);
  const paginationRef = useRef(null);
  const requestIdRef = useRef(0);
  const isLoadingMoreRef = useRef(false);
  const monthSyncFrameRef = useRef(null);
  const historyScrollIntentRef = useRef(false);
  const historyScrollIdleTimerRef = useRef(null);
  const setListNode = useCallback((node) => {
    if (node) {
      listRef.current = node;
    }
  }, []);
  const setPaginationNode = useCallback((node) => {
    if (node) {
      paginationRef.current = node;
    }
  }, []);
  const { history, language } = useContext(TranslationContext);
  const [historyView, setHistoryView] = useState(() => ({
    filters: {
      ...DEFAULT_HISTORY_FILTERS,
      cardIds: [],
    },
    visibleMonthRange: getCurrentHistoryMonthRange(),
    listTransition: DEFAULT_HISTORY_LIST_TRANSITION,
  }));
  const [historyPage, setHistoryPage] = useState({
    type: null,
    filterKey: '',
    items: [],
    isLoaded: false,
    isLoading: false,
    isLoadingMore: false,
    isMore: false,
    lastId: null,
    error: null,
  });
  const { filters, listTransition, visibleMonthRange } = historyView;
  const displayedDateRange = filters.dateRange || visibleMonthRange;
  const hasQuery = filters.query.trim().length > 0;
  const canPollSessionData = isUnlockSessionReady();
  const operationFilters = useMemo(() => getHistoryOperationFilters(filters.type, {
    cardIds: filters.cardIds,
    dateRange: filters.dateRange,
  }), [filters.cardIds, filters.dateRange, filters.type]);
  const operationFilterKey = useMemo(
    () => JSON.stringify(operationFilters || []),
    [operationFilters]
  );
  const filterMetadata = useHistoryFilterMetadata({
    enabled: canPollSessionData,
    dateRange: displayedDateRange,
    cardIds: filters.cardIds,
  });
  const isActivePage = historyPage.type === filters.type
    && historyPage.filterKey === operationFilterKey;
  const activeHistoryPanel = useMemo(() => {
    const transactions = historyPage.type === filters.type
      && historyPage.filterKey === operationFilterKey
      ? historyPage.items
      : [];

    return {
      id: filters.type,
      emptyState: getHistoryEmptyState(filters, history),
      groups: groupTransactionsByDate(transactions, language, history),
      transactions,
    };
  }, [filters, history, historyPage, language, operationFilterKey]);

  useEffect(() => {
    if (!canPollSessionData) {
      return undefined;
    }

    const requestedType = filters.type;
    const requestedFilterKey = operationFilterKey;
    const requestedFilters = operationFilters;
    const requestId = requestIdRef.current + 1;
    let isCurrentRequest = true;

    requestIdRef.current = requestId;
    isLoadingMoreRef.current = false;
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
    setHistoryPage({
      type: requestedType,
      filterKey: requestedFilterKey,
      items: [],
      isLoaded: false,
      isLoading: true,
      isLoadingMore: false,
      isMore: false,
      lastId: null,
      error: null,
    });

    fetchTransactionsPage({
      limit: HISTORY_PAGE_LIMIT,
      filters: requestedFilters,
    })
      .then((page) => {
        if (!isCurrentRequest || requestIdRef.current !== requestId) {
          return;
        }

        setHistoryPage({
          type: requestedType,
          filterKey: requestedFilterKey,
          items: page.items,
          isLoaded: true,
          isLoading: false,
          isLoadingMore: false,
          isMore: Boolean(page.isMore && page.lastId),
          lastId: page.lastId,
          error: null,
        });
      })
      .catch((error) => {
        if (!isCurrentRequest || requestIdRef.current !== requestId) {
          return;
        }

        setHistoryPage({
          type: requestedType,
          filterKey: requestedFilterKey,
          items: [],
          isLoaded: true,
          isLoading: false,
          isLoadingMore: false,
          isMore: false,
          lastId: null,
          error,
        });
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [canPollSessionData, filters.type, operationFilterKey, operationFilters]);

  const loadMoreTransactions = useCallback(async () => {
    if (
      !canPollSessionData
      || !isActivePage
      || historyPage.isLoading
      || historyPage.isLoadingMore
      || isLoadingMoreRef.current
      || !historyPage.isMore
      || !historyPage.lastId
    ) {
      return;
    }

    const requestedType = filters.type;
    const requestedFilterKey = operationFilterKey;
    const requestedLastId = historyPage.lastId;
    const requestId = requestIdRef.current;

    isLoadingMoreRef.current = true;
    setHistoryPage((current) => ({
      ...current,
      isLoadingMore: true,
      error: null,
    }));

    try {
      const page = await fetchTransactionsPage({
        limit: HISTORY_PAGE_LIMIT,
        lastId: requestedLastId,
        filters: operationFilters,
      });

      if (requestIdRef.current !== requestId) {
        return;
      }

      setHistoryPage((current) => {
        if (
          current.type !== requestedType
          || current.filterKey !== requestedFilterKey
        ) {
          return current;
        }

        const existingIds = new Set(current.items.map((transaction) => transaction.id));
        const nextItems = page.items.filter((transaction) => !existingIds.has(transaction.id));
        const didAdvance = Boolean(page.lastId && page.lastId !== requestedLastId);

        return {
          ...current,
          items: [...current.items, ...nextItems],
          isLoadingMore: false,
          isMore: Boolean(page.isMore && didAdvance && nextItems.length > 0),
          lastId: page.lastId || current.lastId,
          error: null,
        };
      });
    } catch (error) {
      if (requestIdRef.current === requestId) {
        setHistoryPage((current) => ({
          ...current,
          isLoadingMore: false,
          isMore: false,
          error,
        }));
      }
    } finally {
      isLoadingMoreRef.current = false;
    }
  }, [
    canPollSessionData,
    filters.type,
    historyPage,
    isActivePage,
    operationFilterKey,
    operationFilters,
  ]);

  useEffect(() => {
    if (
      !isActivePage
      || !historyPage.isMore
      || !paginationRef.current
      || !listRef.current
      || typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !isLoadingMoreRef.current) {
        loadMoreTransactions();
      }
    }, {
      root: listRef.current,
      rootMargin: '0px 0px 140px 0px',
      threshold: 0.1,
    });

    observer.observe(paginationRef.current);

    return () => observer.disconnect();
  }, [historyPage.isMore, isActivePage, loadMoreTransactions]);

  useEffect(() => {
    document.documentElement.classList.add('history-scroll-lock');
    document.body.classList.add('history-scroll-lock');

    return () => {
      document.documentElement.classList.remove('history-scroll-lock');
      document.body.classList.remove('history-scroll-lock');
    };
  }, []);

  useEffect(() => () => {
    if (monthSyncFrameRef.current !== null) {
      window.cancelAnimationFrame(monthSyncFrameRef.current);
    }

    if (historyScrollIdleTimerRef.current !== null) {
      window.clearTimeout(historyScrollIdleTimerRef.current);
    }
  }, []);

  const scheduleHistoryScrollIntentEnd = useCallback((delay = HISTORY_SCROLL_IDLE_DELAY) => {
    if (historyScrollIdleTimerRef.current !== null) {
      window.clearTimeout(historyScrollIdleTimerRef.current);
    }

    historyScrollIdleTimerRef.current = window.setTimeout(() => {
      historyScrollIntentRef.current = false;
      historyScrollIdleTimerRef.current = null;
    }, delay);
  }, []);

  const markHistoryScrollIntent = useCallback(() => {
    historyScrollIntentRef.current = true;
    scheduleHistoryScrollIntentEnd(500);
  }, [scheduleHistoryScrollIntentEnd]);

  const resetHistoryScrollIntent = useCallback(() => {
    historyScrollIntentRef.current = false;

    if (historyScrollIdleTimerRef.current !== null) {
      window.clearTimeout(historyScrollIdleTimerRef.current);
      historyScrollIdleTimerRef.current = null;
    }

    if (monthSyncFrameRef.current !== null) {
      window.cancelAnimationFrame(monthSyncFrameRef.current);
      monthSyncFrameRef.current = null;
    }
  }, []);

  const syncVisibleHistoryMonth = useCallback((listNode) => {
    if (!listNode || listNode !== listRef.current) {
      return;
    }

    const monthKey = getVisibleHistoryMonthKey(listNode);
    const nextMonthRange = getHistoryMonthRange(monthKey);

    if (!nextMonthRange) {
      return;
    }

    setHistoryView((current) => {
      if (
        current.visibleMonthRange?.start === nextMonthRange.start
        && current.visibleMonthRange?.end === nextMonthRange.end
      ) {
        return current;
      }

      return {
        ...current,
        visibleMonthRange: nextMonthRange,
      };
    });
  }, []);

  const handleHistoryListScroll = useCallback((event) => {
    const listNode = event.currentTarget;

    if (!historyScrollIntentRef.current || monthSyncFrameRef.current !== null) {
      return;
    }

    scheduleHistoryScrollIntentEnd();
    monthSyncFrameRef.current = window.requestAnimationFrame(() => {
      monthSyncFrameRef.current = null;
      syncVisibleHistoryMonth(listNode);
    });
  }, [scheduleHistoryScrollIntentEnd, syncVisibleHistoryMonth]);

  const handleHistoryListKeyDown = useCallback((event) => {
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(event.key)) {
      markHistoryScrollIntent();
    }
  }, [markHistoryScrollIntent]);

  useEffect(() => {
    if (activeHistoryPanel.groups.length === 0) {
      return undefined;
    }

    const syncFrame = window.requestAnimationFrame(() => {
      syncVisibleHistoryMonth(listRef.current);
    });

    return () => window.cancelAnimationFrame(syncFrame);
  }, [activeHistoryPanel.groups, syncVisibleHistoryMonth]);

  const handleTypeChange = (type) => {
    if (!Object.values(TRANSACTION_TYPE_FILTERS).includes(type) || type === filters.type) {
      return;
    }

    triggerImpactHaptic('light');
    resetHistoryScrollIntent();
    setHistoryView((current) => {
      return {
        ...current,
        filters: {
          ...current.filters,
          type,
        },
        listTransition: getHistoryListTransition(current.filters.type, type),
      };
    });
  };

  const handleSearchChange = (event) => {
    setHistoryView((current) => ({
      ...current,
      filters: {
        ...current.filters,
        query: event.target.value,
      },
    }));
  };

  const handleDateRangeChange = (dateRange) => {
    setHistoryView((current) => ({
      ...current,
      filters: {
        ...current.filters,
        dateRange,
      },
    }));
  };

  const handleSelectedCardIdsChange = (cardIds) => {
    setHistoryView((current) => ({
      ...current,
      filters: {
        ...current.filters,
        cardIds,
        cardId: 'all',
      },
    }));
  };

  const handleTransactionSelect = (transaction) => {
    navigate(`/history/${transaction.id}`, {
      state: {
        backgroundLocation: location,
        skipRouteOverlayTransition: true,
        transaction: getTransactionRouteSnapshot(transaction),
      },
    });
  };

  return (
    <section className="history">
      <div className="history__content">
        <div className="history__chrome">
          <header className="history__header">
            <h1 className="history__title">{history.title}</h1>
          </header>

          {HISTORY_ADVANCED_TOOLS_ENABLED && (
            <div className="history__search-field">
              <input
                type="search"
                className="history__search-input"
                placeholder={history.searchPlaceholder}
                value={filters.query}
                onChange={handleSearchChange}
              />
            </div>
          )}

          <HistoryFilters
            cards={filterMetadata.cards}
            dateRange={displayedDateRange}
            isDateRangeUserSelected={Boolean(filters.dateRange)}
            minDate={filterMetadata.minDate}
            selectedCardIds={filters.cardIds}
            onDateRangeChange={handleDateRangeChange}
            onSelectedCardIdsChange={handleSelectedCardIdsChange}
          />

          <HistorySummaryFilter
            activeType={filters.type}
            expenseAmount={filterMetadata.isLoaded
              ? filterMetadata.stats.outcome.formatted
              : ''}
            incomeAmount={filterMetadata.isLoaded
              ? filterMetadata.stats.income.formatted
              : ''}
            initialLoading={filterMetadata.isLoading && !filterMetadata.isLoaded}
            labels={{
              ariaLabel: history.totalsAria,
              expense: history.spending,
              income: history.income,
              selectExpenseAria: history.showSpendingAria,
              selectIncomeAria: history.showIncomeAria,
              clearAria: history.showAllTransactionsAria,
              loadingAmountAria: history.loadingTotalsAria,
            }}
            loading={filterMetadata.isLoading || filterMetadata.isRefreshing}
            onChange={handleTypeChange}
          />

          {HISTORY_ADVANCED_TOOLS_ENABLED && hasQuery && (
            <p className="history__search-summary" />
          )}
        </div>

        <div className="history__list-viewport">
          <AnimatePresence
            initial={false}
            custom={listTransition}
          >
            <motion.div
              ref={setListNode}
              key={activeHistoryPanel.id}
              className="history__list"
              custom={listTransition}
              variants={HISTORY_LIST_VARIANTS}
              initial={shouldReduceMotion ? false : 'enter'}
              animate="center"
              exit={shouldReduceMotion ? undefined : 'exit'}
              transition={shouldReduceMotion
                ? { duration: 0 }
                : HISTORY_PANEL_TRANSITION}
              onScroll={handleHistoryListScroll}
              onPointerDown={markHistoryScrollIntent}
              onTouchStart={markHistoryScrollIntent}
              onWheel={markHistoryScrollIntent}
              onKeyDown={handleHistoryListKeyDown}
            >
              <div className="history__list-content">
                {!isActivePage || (historyPage.isLoading && !historyPage.isLoaded) ? (
                  <div className="history__empty">
                    <MiniPreloader label={history.loading} />
                  </div>
                ) : activeHistoryPanel.transactions.length === 0 ? (
                  <div className="history__empty">
                    <div className="history__empty-icon" aria-hidden="true">
                      <ShootingStarIcon className="history__empty-icon-svg" />
                    </div>
                    <p className="history__empty-title">
                      {activeHistoryPanel.emptyState.title}
                    </p>
                    <p className="history__empty-text">
                      {activeHistoryPanel.emptyState.text}
                    </p>
                  </div>
                ) : (
                  activeHistoryPanel.groups.map((group) => (
                    <section
                      key={group.key}
                      className="history__group"
                      data-history-month={getHistoryGroupMonthKey(group)}
                    >
                      <h2 className="history__group-title">
                        {group.label}
                      </h2>
                      <TxCard
                        transactions={group.items}
                        onSelect={handleTransactionSelect}
                        showCardContext
                      />
                    </section>
                  ))
                )}

                <div ref={setPaginationNode} className="history__pagination">
                  {historyPage.isLoadingMore ? (
                    <MiniPreloader label={history.loading} />
                  ) : null}
                  {!historyPage.isLoadingMore && historyPage.isMore ? (
                    <button
                      type="button"
                      className="history__load-more"
                      onClick={loadMoreTransactions}
                    >
                      {history.loadMore}
                    </button>
                  ) : null}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

export default History;
